import React, { FC } from "react";
import { Loader } from "@shared/components";

import "./index.scss";

// Design-fix plan: the generic failure copy shared by every leaderboard panel that has nothing more
// specific to say -- exported so callers reuse the SAME sentence instead of each writing their own
// copy of it (LeaderboardsContainer.tsx's ranked-board panel and OverviewLeaderboard.tsx both did,
// verbatim). A panel with more specific copy (e.g. the crowns row's "We couldn't load the crowns...")
// still passes its own `failureMessage` instead of this constant.
export const LEADERBOARD_LOAD_FAILED_MESSAGE = "We couldn't load the leaderboard. Refresh to try again.";

interface LeaderboardLoadStatusProps {
  isLoading: boolean;
  loadFailed: boolean;
  failureTestId: string;
  failureMessage: string;
  // loader-dedup plan (single-loader Overview fix): when true, the centered Loader below does not
  // render even while `isLoading` is true -- OverviewLeaderboard.tsx passes its caller's (MySparksContainer's)
  // own page-level `isLoading` through here so the Overview rail card never stacks its loader on top of
  // the page loader. Defaults to false (show) -- LeaderboardsContainer.tsx, this component's other
  // consumer (both its ranked-board and crowns panels), never passes it and is unaffected by
  // construction. Suppresses ONLY the Loader -- the failure message below keeps its own
  // `loadFailed && !isLoading` guard, untouched by this prop, so a fetch that fails while suppressed
  // still says so.
  suppressLoader?: boolean;
}

/**
 * The loading/failure guard shared by every leaderboard panel that fetches its own data --
 * originally file-local to `LeaderboardsContainer.tsx` (the tab's ranked-board and crowns panels),
 * extracted here (overview-leaderboard-card-plan.md Part 2) so the Overview rail card's own fetch can
 * reuse the SAME guard rather than a second copy drifting from it. A pure, presentational component:
 * four primitive props, no slice coupling.
 *
 * Shows a centered `Loader` while a fetch is in flight, then (once it's settled) a failure message if
 * that fetch failed. `loadFailed && !isLoading` is the load-bearing detail, not `loadFailed` alone: a
 * stale failure flag from a previous fetch must not flash a failure message while a fresh retry is
 * already loading.
 *
 * @param props.isLoading - whether this caller's own fetch is currently in flight.
 * @param props.loadFailed - whether this caller's own last fetch failed.
 * @param props.failureTestId - the `data-testid` to render on the failure message, owned by the
 *   caller so each panel's own regression tests keep asserting on their own stable id.
 * @param props.failureMessage - the copy to render inside the failure message.
 * @param props.suppressLoader - when true, hides the Loader even while `isLoading` -- never the
 *   failure message. Defaults to false. See the prop's own comment on the interface above.
 */
const LeaderboardLoadStatus: FC<LeaderboardLoadStatusProps> = (props) => {
  const { isLoading, loadFailed, failureTestId, failureMessage, suppressLoader } = props;

  return (
    <>
      {isLoading && !suppressLoader ? <Loader position="center" /> : null}
      {loadFailed && !isLoading ? (
        <div className="leaderboards-load-failed" data-testid={failureTestId}>
          {failureMessage}
        </div>
      ) : null}
    </>
  );
};

export default LeaderboardLoadStatus;
